// Package imports:
import React, { useState } from 'react';
import cx from 'classnames';
import NumberFormat, { NumberFormatProps } from 'react-number-format';
import { faEye, faEyeSlash } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// Service imports:
import { getCurrencySymbol } from '../../services/utils';

type InputType = 'input' | 'numberFormat' | 'textArea' | 'password';
type InputSizes = 'sm' | 'lg';
type TextAlign = 'left' | 'center' | 'right';

type InputProps = React.InputHTMLAttributes<HTMLInputElement>;
type TextAreaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;
interface IOwnProps {
    inputType?: InputType,
    inputSize?: InputSizes,
    textAlign?: TextAlign,
    label?: string,
    shortLabel?: string,
    visible?: boolean,
    toggleVisible?(): void,
    currency?: string,
};

type Props = IOwnProps & NumberFormatProps & InputProps & TextAreaProps;

const Input: React.FC<Props> = ({
    // Custom props
    inputType = 'input',
    inputSize = 'lg',
    textAlign = 'left',
    label,
    shortLabel,
    currency, 

    // Element props
    id,
    className,
    style,
    onFocus,
    onBlur,
    value,

    visible,
    toggleVisible,

    ...props
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const focus = () => setIsFocused(true);
    const blur = () => setIsFocused(false);

    const [showPassword, setShowPassword] = useState(false);
    const togglePassword = () => setShowPassword(!showPassword);

    // Props that overwrite native element props.
    const elementProps = {
        id,
        value,
        style: {
            ...style,
            textAlign: textAlign
        },
        onFocus: (e: React.FocusEvent<HTMLInputElement & HTMLTextAreaElement, Element>) => {
            if (onFocus) onFocus(e);
            focus();
        },
        onBlur: (e: React.FocusEvent<HTMLInputElement & HTMLTextAreaElement, Element>) => {
            if (onBlur) onBlur(e);
            blur();
        }
    }

    return (
        <div className={cx('KCL_input', inputSize, className, {
            'is-focused': isFocused,
            'has-value': (value !== undefined && value !== null && value.toString().length > 0),
            'is-disabled': props.disabled,
            'currency_input': currency
        })}>
            {inputType === 'numberFormat'
                ? <>
                    <NumberFormat
                        {...elementProps}
                        {...(props as NumberFormatProps)}
                    />
                    {currency &&
                        <span className={cx('currency', currency)}>
                            {getCurrencySymbol(currency)}
                        </span>
                    }
                </>
                : inputType === 'input'
                ? <input
                    {...elementProps}
                    {...(props as InputProps)}
                />
                : inputType === "password" 
                    ? <>
                        <input
                            type={showPassword ? 'text' : 'password'}
                            {...elementProps}
                            {...(props as InputProps)}
                        />
                        {
                            (elementProps.value !== '') && <>
                                <FontAwesomeIcon icon={faEyeSlash} className={cx("icon-fa", "eye-icon", {'visible': !showPassword})} onClick={togglePassword} />
                                <FontAwesomeIcon icon={faEye} className={cx("icon-fa", "eye-icon", {'visible': showPassword})} onClick={togglePassword} />
                            </>
                        }
                    </>
                : <textarea
                    {...elementProps}
                    {...(props as TextAreaProps)}
                />
            }
            {label && (
                <label
                    className="input__label"
                    htmlFor={id}
                >
                    <span className="main-label">{label}</span>
                    <span className="short-label">{shortLabel ?? label}</span>
                </label>
            )}
        </div>
    );
}

export default (Input);